import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogoMark } from "./Logo";
import { useAuth } from "../context/AuthContext";

const LINKS = [
  { to: "/explore", label: "Explore", n: "01" },
  { to: "/listen", label: "Listen", n: "02" },
  { to: "/subscribe", label: "Subscribe", n: "03" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, login, logout, loading } = useAuth();
  const [acct, setAcct] = useState(false);
  useEffect(() => { setOpen(false); setAcct(false); }, [pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 mix-blend-difference text-[var(--bone)]" data-testid="nav-container">
      <div className="flex items-center justify-between px-5 sm:px-8 lg:px-12 h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-home-link" aria-label="Outlaw Creative home">
          <LogoMark size={32} />
          <span className="leading-none">
            <span className="f-display font-800 text-lg tracking-wide block font-extrabold">OUTLAW CREATIVE</span>
            <span className="f-serif italic text-[13px] opacity-80">(the geometry of life)</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}-link`}
              className={({ isActive }) => `f-mono text-xs uppercase tracking-[0.2em] flex items-baseline gap-2 link-u pb-1 ${isActive ? "opacity-100" : "opacity-75 hover:opacity-100"}`}
            >
              <span className="text-[10px] opacity-60">{l.n}</span>{l.label}
            </NavLink>
          ))}
          {!loading && (user ? (
            <div className="relative">
              <button onClick={() => setAcct((a) => !a)} className="flex items-center gap-2 opacity-80 hover:opacity-100" data-testid="nav-account-button">
                {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full grayscale" /> : <span className="w-7 h-7 rounded-full border border-current flex items-center justify-center f-mono text-[11px] uppercase">{(user.name || user.email || "?")[0]}</span>}
                <span className="sr-only">Account</span>
              </button>
              {acct && (
                <div className="absolute right-0 top-10 w-56 bg-[var(--ink)] border border-[var(--line)] p-2 flex flex-col" data-testid="nav-account-menu">
                  <span className="px-3 py-2 f-mono text-[11px] text-[var(--ash)] truncate">{user.email}</span>
                  <Link to="/saved" className="px-3 py-2 f-mono text-xs uppercase tracking-[0.15em] hover:text-[var(--signal)]" data-testid="nav-saved-link">Saved</Link>
                  {user.is_admin && <Link to="/admin" className="px-3 py-2 f-mono text-xs uppercase tracking-[0.15em] hover:text-[var(--signal)]" data-testid="nav-admin-link">Admin</Link>}
                  <button onClick={logout} className="px-3 py-2 text-left f-mono text-xs uppercase tracking-[0.15em] hover:text-[var(--signal)]" data-testid="nav-logout-button">Sign out</button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={login} className="f-mono text-xs uppercase tracking-[0.2em] opacity-75 hover:opacity-100 link-u pb-1" data-testid="nav-login-button">Sign in</button>
          ))}
        </nav>
        <button className="md:hidden f-mono text-xs uppercase tracking-[0.2em] py-3 px-1" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-expanded={open}>
          {open ? "Close" : "Menu"}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            className="md:hidden fixed inset-0 top-20 bg-[var(--ink)] px-6 pt-10 mix-blend-normal"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: [0.7, 0, 0.2, 1] }}
            data-testid="nav-mobile-menu"
          >
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="flex items-baseline gap-4 border-b border-[var(--line)] py-6" data-testid={`nav-mobile-${l.label.toLowerCase()}-link`}>
                <span className="f-mono text-xs text-[var(--ash)]">{l.n}</span>
                <span className="f-display font-black text-6xl uppercase">{l.label}</span>
              </Link>
            ))}
            <div className="flex flex-wrap gap-6 pt-8 f-mono text-xs uppercase tracking-[0.2em]">
              {user ? (
                <>
                  <Link to="/saved" data-testid="nav-mobile-saved-link">Saved</Link>
                  {user.is_admin && <Link to="/admin" data-testid="nav-mobile-admin-link">Admin</Link>}
                  <button onClick={logout} data-testid="nav-mobile-logout-button">Sign out</button>
                </>
              ) : (
                <button onClick={login} data-testid="nav-mobile-login-button">Sign in with Google</button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
